import { forwardRef, useEffect, useRef } from "react";
import { ArrowUp, LoaderCircle, Mic, MicOff, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/** Sticky composer: autosizing Textarea + mic (speech-to-text) + send + Read-aloud switch, with an a11y live region. */
export const BuiltBuddyComposer = forwardRef(function BuiltBuddyComposer(
  { value, onChange, onSend, busy, listening, onToggleMic, micSupported, micBlocked, readAloud, onReadAloudChange, ttsSupported, liveText, disabledReason, reading = false, onStopReading, placeholder },
  ref
) {
  const innerRef = useRef(null);
  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };
  // autosize 1–4 rows
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(128, Math.max(44, el.scrollHeight))}px`;
  }, [value]);

  const canSend = !busy && !!value.trim() && !disabledReason;
  const micTitle = !micSupported ? "Voice input isn't supported in this browser" : micBlocked ? "Microphone permission blocked" : listening ? "Stop and send" : "Ask by voice";

  return (
    <div className="sticky bottom-0 border-t border-border bg-card/90 px-3 pb-3 pt-2.5 backdrop-blur supports-[backdrop-filter]:bg-card/70" data-testid="builtbuddy-panel-footer">
      <div className="flex items-end gap-1.5">
        <Textarea
          ref={setRefs}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (canSend) onSend();
            }
          }}
          placeholder={listening ? "Listening… speak your question" : placeholder || "Ask BuiltBuddy… e.g. ‘What size are the pad footings on S-03?’"}
          className={`min-h-[44px] max-h-32 resize-none text-sm leading-5 ${listening ? "ring-2 ring-[hsl(var(--info))]" : ""}`}
          aria-label="Ask BuiltBuddy"
          disabled={busy && !value}
          data-testid="builtbuddy-composer-input"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                type="button"
                variant={listening ? "default" : "secondary"}
                size="icon"
                className={`h-11 w-11 shrink-0 ${listening ? "ring-2 ring-[hsl(var(--info))] ring-offset-2 ring-offset-background" : ""}`}
                onClick={onToggleMic}
                disabled={!micSupported || micBlocked || (busy && !listening)}
                aria-pressed={listening}
                aria-label={micTitle}
                data-testid="builtbuddy-voice-mic-button"
              >
                {!micSupported || micBlocked ? <MicOff className="h-4 w-4" /> : <Mic className={`h-4 w-4 ${listening ? "animate-pulse" : ""}`} />}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {micTitle}
          </TooltipContent>
        </Tooltip>
        <Button type="button" size="icon" className="h-11 w-11 shrink-0" onClick={onSend} disabled={!canSend} aria-label="Send question" data-testid="builtbuddy-composer-send-button">
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </Button>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground" title={ttsSupported ? "Read every answer aloud" : "Read aloud isn't supported in this browser"}>
          <Switch checked={readAloud} onCheckedChange={onReadAloudChange} disabled={!ttsSupported} className="scale-90" data-testid="builtbuddy-voice-readaloud-switch" />
          Read aloud
        </label>
        <span className="truncate text-[10px] text-muted-foreground" data-testid="builtbuddy-composer-hint">
          {reading ? (
            <button type="button" onClick={onStopReading} className="inline-flex items-center gap-1 font-medium text-[hsl(var(--success))] underline-offset-2 hover:underline" data-testid="builtbuddy-composer-stop-reading">
              <Volume2 className="h-3 w-3" /> Reading the new RFI replies aloud · Stop
            </button>
          ) : (
            disabledReason || (listening ? "Tap the mic again to send" : "Enter to send · Shift+Enter for a new line")
          )}
        </span>
      </div>
      <span className="sr-only" aria-live="polite" data-testid="builtbuddy-voice-live-region">
        {liveText}
      </span>
    </div>
  );
});
